import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

/**
 * Counter — animates a numeric value from 0 to target when scrolled into view.
 * Preserves any non-numeric prefix/suffix in the original string (e.g. "20K+" → animates 0 → 20 with "K+" suffix).
 */
const Counter = ({ value, duration = 1600, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });

  // Parse the value: capture numeric portion and remaining string.
  const match = String(value).match(/^([\d.,]+)(.*)$/);
  const numeric = match ? parseFloat(match[1].replace(/,/g, "")) : null;
  const suffix = match ? match[2] : String(value);
  const hasDecimal = numeric !== null && !Number.isInteger(numeric);

  const [display, setDisplay] = useState(numeric === null ? String(value) : "0");

  useEffect(() => {
    if (!inView || numeric === null) return;
    const start = performance.now();
    let rafId;

    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      const current = numeric * eased;
      setDisplay(hasDecimal ? current.toFixed(1) : Math.round(current).toString());
      if (t < 1) rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [inView, numeric, duration, hasDecimal]);

  return (
    <motion.span ref={ref} className={className}>
      {display}
      {suffix}
    </motion.span>
  );
};

export default Counter;
